// Component mapping utility for restoring embedded components from chat history
import { SkillsConfirmationReadOnly } from '../components/SkillsConfirmation';
import { LocationSelectionReadOnly } from './topicStrategies/location/components';
import { ExperienceHistogramReadOnly } from './topicStrategies/experience/components';
import { CompanyStageConfirmationReadOnly } from './topicStrategies/companyStage/components';
import { TitleConfirmationReadOnly, TitleOptions } from './topicStrategies/title/components';
import ContinueDiscoveryButton from '../components/ContinueDiscoveryButton';
// Import template components for session restoration
import { 
  SingleRowSelectorTemplate, 
  MultiRowSelectorTemplate, 
  DistributionBarChartTemplate, 
  InlineSliderTemplate 
} from '../components/templates';

/**
 * Component mapping registry
 * Maps component names (strings) to actual React components
 * This allows us to serialize component references in chat history
 * and restore them when loading from the database
 */
export const COMPONENT_MAPPING = {
  // Legacy read-only components
  'SkillsConfirmationReadOnly': SkillsConfirmationReadOnly,
  'LocationSelectionReadOnly': LocationSelectionReadOnly,
  'ExperienceHistogramReadOnly': ExperienceHistogramReadOnly,
  'CompanyStageConfirmationReadOnly': CompanyStageConfirmationReadOnly,
  'TitleConfirmationReadOnly': TitleConfirmationReadOnly,
  'TitleOptions': TitleOptions,
  'ContinueDiscoveryButton': ContinueDiscoveryButton,
  // Template components for session restoration
  'SingleRowSelectorTemplate': SingleRowSelectorTemplate,
  'MultiRowSelectorTemplate': MultiRowSelectorTemplate,
  'DistributionBarChartTemplate': DistributionBarChartTemplate,
  'InlineSliderTemplate': InlineSliderTemplate,
  // Add more components as needed
};

/**
 * Serialize a component reference for storage in chat history
 * @param {React.Component} component - The React component
 * @returns {string} - The component name string
 */
export function serializeComponent(component) {
  // Find the component name in the mapping
  for (const [name, comp] of Object.entries(COMPONENT_MAPPING)) {
    if (comp === component) {
      return name;
    }
  }
  
  // If not found, try to get the component name directly
  if (component && component.name) {
    return component.name;
  }
  
  console.warn('Component not found in mapping:', component);
  return null;
}

/**
 * Deserialize a component reference from chat history
 * @param {string} componentName - The component name string
 * @returns {React.Component|null} - The React component or null if not found
 */
export function deserializeComponent(componentName) {
  if (!componentName || typeof componentName !== 'string') {
    return null;
  }
  
  const component = COMPONENT_MAPPING[componentName];
  if (!component) {
    console.warn('Component not found in mapping:', componentName);
    return null;
  }
  
  return component;
}

/**
 * Process messages to serialize components before saving to database
 * @param {Array} messages - Array of message objects
 * @returns {Array} - Array of messages with serialized components
 */
export function serializeMessagesForStorage(messages) {
  return messages.map(message => {
    if (message.component) {
      return {
        ...message,
        component: serializeComponent(message.component),
        _hasComponent: true // Flag to indicate this message had a component
      };
    }
    return message;
  });
}

/**
 * Process messages to deserialize components after loading from database
 * @param {Array} messages - Array of message objects from database
 * @returns {Array} - Array of messages with restored components
 */
export function deserializeMessagesFromStorage(messages) {
  if (!Array.isArray(messages)) {
    return [];
  }
  
  console.log(`🔧 DESERIALIZING ${messages.length} messages`);
  
  return messages.map((message, index) => {
    if (message._hasComponent && message.component && typeof message.component === 'string') {
      console.log(`🔧 DESERIALIZING MESSAGE ${index}:`, {
        id: message.id,
        component: message.component,
        sessionRestored: message.sessionRestored,
        _hasComponent: message._hasComponent
      });
      
      const restoredComponent = deserializeComponent(message.component);
      console.log(`🔧 RESTORED COMPONENT:`, restoredComponent ? 'SUCCESS' : 'FAILED');
      
      if (restoredComponent) {
        return {
          ...message,
          component: restoredComponent
        };
      } else {
        // If component can't be restored, remove it to avoid errors
        console.warn('Could not restore component for message:', message.component);
        const { component, _hasComponent, ...messageWithoutComponent } = message;
        return messageWithoutComponent;
      }
    }
    return message;
  });
}

/**
 * Create a message with an embedded component
 * @param {string} role - Message role ('User' or 'AI')
 * @param {string} [content] - Message content (optional - can be null/undefined for component-only messages)
 * @param {React.Component} component - React component to embed
 * @param {Object} componentProps - Props for the component
 * @param {string} [id] - Optional message ID
 * @returns {Object} - Message object ready for storage
 */
export function createMessageWithComponent(role, content = null, component, componentProps, id = null) {
  const message = {
    role,
    component: serializeComponent(component),
    componentProps,
    _hasComponent: true,
    timestamp: Date.now()
  };

  // Only add content if it's provided and not empty
  if (content && content.trim() !== '') {
    message.content = content;
  }

  // Add ID if provided
  if (id) {
    message.id = id;
  }

  return message;
}
